import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

function money(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function currentMonth() {
  return String(new Date().getMonth() + 1);
}

function currentYear() {
  return String(new Date().getFullYear());
}

function tone(value) {
  return Number(value || 0) >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800';
}

function ChartBars({ items, valueKey, labelKey = 'date', currency = 'UGX', color = 'bg-emerald-600' }) {
  const max = Math.max(...(items || []).map((item) => Math.abs(Number(item?.[valueKey] || 0))), 1);
  return (
    <div className="flex items-end gap-2 h-56 overflow-x-auto pb-2">
      {(items || []).map((item) => {
        const value = Number(item?.[valueKey] || 0);
        const height = Math.max(8, (Math.abs(value) / max) * 100);
        return (
          <div key={item[labelKey] || `${value}-${Math.random()}`} className="min-w-[44px] flex flex-col items-center justify-end gap-2">
            <div className="text-[11px] text-slate-500 leading-none">{money(value, currency).replace(`${currency} `, '')}</div>
            <div className="w-6 h-36 flex items-end">
              <div className={`w-full rounded-t-lg ${color} ${value < 0 ? 'opacity-60' : ''}`} style={{ height: `${height}%` }} />
            </div>
            <div className="text-[10px] text-slate-500 leading-tight text-center whitespace-nowrap">{String(item[labelKey] || '').slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function BudgetCashFlow() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    month: currentMonth(),
    year: currentYear(),
    branch_id: 'all',
    date_from: '',
    date_to: '',
  });

  const load = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        month: nextFilters.date_from || nextFilters.date_to ? undefined : nextFilters.month || undefined,
        year: nextFilters.date_from || nextFilters.date_to ? undefined : nextFilters.year || undefined,
        branch_id: nextFilters.branch_id && nextFilters.branch_id !== 'all' ? nextFilters.branch_id : undefined,
        date_from: nextFilters.date_from || undefined,
        date_to: nextFilters.date_to || undefined,
      };
      const res = await api.get('/budget/cash-flow', { params });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to load cash flow');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branches = useMemo(() => Array.isArray(data?.branches) ? data.branches : [], [data?.branches]);
  const activeBranches = useMemo(() => branches.filter((branch) => String(branch?.status || 'active').toLowerCase() === 'active'), [branches]);
  const singleBranch = activeBranches.length <= 1 || !!data?.single_branch;
  const charts = data?.charts || {};
  const summaryCards = Array.isArray(data?.summary_cards) ? data.summary_cards : [];
  const currency = user?.default_currency || data?.current_currency || 'UGX';

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    load(next);
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (data?.upgrade_required) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Cash Flow</h1>
          <p className="text-sm text-slate-500">Upgrade your package to unlock Budget and Financial Intelligence.</p>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">{data?.message || 'Budget access is required.'}</div>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => window.location.assign('/dashboard/package')}>View Packages</Button>
        </CardContent>
      </Card>
    );
  }

  const incomeVsExpenses = Array.isArray(charts.income_vs_expenses) ? charts.income_vs_expenses : [];
  const netCashFlow = Array.isArray(charts.net_cash_flow) ? charts.net_cash_flow : [];
  const balanceTrend = Array.isArray(charts.balance_trend) ? charts.balance_trend : [];
  const categoryContribution = Array.isArray(charts.category_contribution) ? charts.category_contribution : [];
  const monthlyComparison = Array.isArray(charts.monthly_comparison) ? charts.monthly_comparison : [];
  const hasCashFlowData = incomeVsExpenses.length > 0 || netCashFlow.length > 0 || balanceTrend.length > 0 || categoryContribution.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Cash Flow</h1>
          <p className="text-sm text-slate-500 mt-0.5">Monitor income, expenses, net flow, and balance movement over time.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-200" onClick={() => load()}>
            Refresh
          </Button>
        </div>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
          <Input type="number" min="1" max="12" value={filters.month} onChange={(e) => updateFilter('month', e.target.value)} placeholder="Month" className="bg-white" />
          <Input type="number" min="2020" max="2100" value={filters.year} onChange={(e) => updateFilter('year', e.target.value)} placeholder="Year" className="bg-white" />
          {!singleBranch ? (
            <Select value={filters.branch_id} onValueChange={(value) => updateFilter('branch_id', value)}>
              <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-600">{activeBranches[0]?.name || 'Main Branch'}</div>
          )}
          <Input type="date" value={filters.date_from} onChange={(e) => updateFilter('date_from', e.target.value)} className="bg-white" />
          <Input type="date" value={filters.date_to} onChange={(e) => updateFilter('date_to', e.target.value)} className="bg-white" />
          <div className="flex items-center h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-xs text-slate-500">Date range overrides month/year.</div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {summaryCards.map((card) => (
          <Card key={card.label} className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">{card.label}</p>
              <p className={`mt-2 text-2xl font-bold mono-nums ${Number(card.value || 0) < 0 ? 'text-red-600' : 'text-slate-900'}`}>{money(card.value, currency)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {!hasCashFlowData ? (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-8 text-center text-slate-500">
            No cash flow data found for the selected period. Add income and expense transactions to see trends here.
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Income vs Expenses</CardTitle></CardHeader>
          <CardContent>
            <ChartBars items={incomeVsExpenses} valueKey="income" currency={currency} color="bg-emerald-600" />
            <ChartBars items={incomeVsExpenses} valueKey="expenses" currency={currency} color="bg-red-500" />
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Net Cash Flow</CardTitle></CardHeader>
          <CardContent><ChartBars items={netCashFlow} valueKey="net" currency={currency} color="bg-blue-600" /></CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Balance Trend</CardTitle></CardHeader>
          <CardContent><ChartBars items={balanceTrend} valueKey="balance" currency={currency} color="bg-emerald-600" /></CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Category Contribution</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {categoryContribution.map((item) => (
              <div key={item.name} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">{item.name}</span>
                  <span className="text-slate-500">{money(item.amount, currency)}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(4, (Number(item.amount || 0) / Math.max(...categoryContribution.map((c) => Number(c.amount || 0)), 1)) * 100))}%`, backgroundColor: item.color || '#0f766e' }} />
                </div>
              </div>
            ))}
            {categoryContribution.length === 0 ? <p className="text-sm text-slate-500">No category data for this period.</p> : null}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Cash Flow Summary</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {summaryCards.map((item) => (
              <div key={item.label} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                <span className="text-sm text-slate-600">{item.label}</span>
                <span className={`text-sm font-semibold ${tone(item.value)}`}>{money(item.value, currency)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Advice and Alerts</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {(data?.insights || []).map((item, index) => <div key={index} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">{item}</div>)}
            {(data?.warnings || []).map((item, index) => (
              <div key={index} className={`rounded-xl border p-3 text-sm ${String(item.type) === 'danger' ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                <div className="font-semibold">{item.title}</div>
                <div>{item.message}</div>
              </div>
            ))}
            {(data?.advice || []).map((item, index) => <div key={`advice-${index}`} className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">{item}</div>)}
            {(!data?.insights?.length && !data?.warnings?.length && !data?.advice?.length) ? <p className="text-sm text-slate-500">No advice available for this period.</p> : null}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Comparison</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="py-3 pr-4 font-medium">Month</th>
                <th className="py-3 pr-4 font-medium">Income</th>
                <th className="py-3 pr-4 font-medium">Expenses</th>
                <th className="py-3 pr-4 font-medium">Net</th>
              </tr>
            </thead>
            <tbody>
              {monthlyComparison.map((item) => (
                <tr key={item.month} className="border-b border-slate-50 last:border-0">
                  <td className="py-3 pr-4 text-slate-700">{item.month}</td>
                  <td className="py-3 pr-4 text-slate-700">{money(item.income, currency)}</td>
                  <td className="py-3 pr-4 text-slate-700">{money(item.expenses, currency)}</td>
                  <td className="py-3 pr-4 text-slate-700">{money(item.net, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {monthlyComparison.length === 0 ? <div className="py-8 text-center text-slate-500">No monthly comparison data yet.</div> : null}
        </CardContent>
      </Card>
    </div>
  );
}
